<template>
  <div class="editor-section">
    <p class="section-desc">圖片輪播支援多張圖片橫向滑動，每張可設定點擊動作。</p>
    <p v-if="items.length > 1" class="carousel-multi-hint" role="status">
      <i class="ri-arrow-left-right-line" aria-hidden="true"></i>
      左右滑動或拖曳可查看更多
    </p>

    <div class="carousel-items">
      <div v-for="(item, idx) in items" :key="idx" class="carousel-item-card">
        <div class="carousel-item-header">
          <span>圖片 {{ idx + 1 }}</span>
          <button type="button" class="icon-btn danger" @click="removeItem(idx)" :disabled="items.length <= 1">
            <i class="ri-delete-bin-line"></i>
          </button>
        </div>

        <div class="form-group">
          <ImageUploader
            :modelValue="item.imageUrl"
            @update:modelValue="updateItem(idx, 'imageUrl', $event)"
            :upload-fn="uploadFn"
            shape="card"
            accept="image/jpeg,image/png"
            placeholder="上傳圖片"
            hint="僅支援 JPEG / PNG（LINE 不接受 WebP）"
            size-hint="最大 10MB"
            :max-size-mb="10"
          />
        </div>

        <div class="form-group">
          <label class="form-label">圖片標題</label>
          <input
            type="text"
            class="form-control"
            :value="item.label"
            @input="updateItem(idx, 'label', $event.target.value)"
            placeholder="例如：查看詳情"
          />
        </div>

        <div class="form-group">
          <label class="form-label">點擊動作</label>
          <LineActionField
            :model-value="itemAction(item)"
            @update:model-value="updateItemAction(idx, $event)"
          />
        </div>
      </div>
    </div>

    <button
      v-if="items.length < 10"
      type="button"
      class="btn btn-primary btn-block btn-add-carousel"
      @click="addItem"
    >
      <i class="ri-add-circle-line"></i> 新增圖片（{{ items.length }}/10）
    </button>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import ImageUploader from '@/components/ImageUploader.vue';
import LineActionField from '@/components/LineActionField.vue';
import { lineMsgTemplateService } from '@/api/modules/lineMsgTemplates.js';

const props = defineProps({ modelValue: { type: Object, required: true } });
const emit = defineEmits(['update:modelValue']);

const uploadFn = (file) => lineMsgTemplateService.uploadImage(file);

const EMPTY_ACTION = { actionType: 'uri', uri: '', text: '', data: '', displayText: '', phone: '', shareText: '' };

const defaultItem = () => ({ imageUrl: '', label: '', ...EMPTY_ACTION });

const items = computed(() => {
  const cols = props.modelValue.columns;
  if (!cols || cols.length === 0) return [defaultItem()];
  return cols.map(c => ({ ...EMPTY_ACTION, ...c }));
});

function updateItem(idx, key, val) {
  const arr = [...items.value];
  arr[idx] = { ...arr[idx], [key]: val };
  emit('update:modelValue', { ...props.modelValue, columns: arr });
}

/** 取出 LineActionField 需要的動作欄位（圖片、標題不傳進去） */
function itemAction(item) {
  const { actionType, uri, text, data, displayText, phone, shareText } = item;
  return { actionType, uri, text, data, displayText, phone, shareText };
}

function updateItemAction(idx, action) {
  const arr = [...items.value];
  arr[idx] = { ...arr[idx], ...action };
  emit('update:modelValue', { ...props.modelValue, columns: arr });
}

function addItem() {
  const arr = [...items.value, defaultItem()];
  emit('update:modelValue', { ...props.modelValue, columns: arr });
}

function removeItem(idx) {
  const arr = items.value.filter((_, i) => i !== idx);
  emit('update:modelValue', { ...props.modelValue, columns: arr });
}
</script>

<style scoped>
.carousel-multi-hint {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0 0 12px;
  padding: 10px 12px;
  font-size: 13px;
  color: #4b5563;
  background: #f3f4f6;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
}
.carousel-multi-hint i { font-size: 16px; color: #6b7280; flex-shrink: 0; }
.carousel-items { display: flex; flex-direction: column; gap: 12px; margin-bottom: 12px; }
.carousel-item-card {
  border: 1px solid #e5e7eb; border-radius: 8px; padding: 12px;
}
.carousel-item-header {
  display: flex; justify-content: space-between; align-items: center;
  margin-bottom: 10px; font-weight: 600; font-size: 14px; color: #374151;
}
.icon-btn {
  width: 28px; height: 28px; border: 1px solid #d1d5db; border-radius: 6px;
  background: #fff; cursor: pointer; display: flex; align-items: center; justify-content: center;
  color: #6b7280; font-size: 14px;
}
.icon-btn.danger:hover:not(:disabled) { border-color: #ef4444; color: #ef4444; }
.icon-btn:disabled { opacity: .35; cursor: not-allowed; }

/* 與 Editor.vue「訊息內容」區塊表單一致 */
.section-desc { font-size: 13px; color: #6b7280; margin: 0 0 12px; }
.form-group { margin-bottom: 14px; }
.form-group:last-child { margin-bottom: 0; }
.form-label { display: block; font-size: 14px; font-weight: 500; color: #374151; margin-bottom: 6px; }
.form-control {
  width: 100%; padding: 8px 12px; border: 1px solid #d1d5db; border-radius: 6px;
  font-size: 14px; box-sizing: border-box;
}
.form-control:focus { outline: none; border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1); }

.btn-add-carousel {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-top: 4px;
}

</style>
